import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, switchMap, tap } from 'rxjs';
import Pusher from 'pusher-js';
import { environment } from '../../environments/environment';
import { AuthService } from '@auth0/auth0-angular';

@Injectable({
    providedIn: 'root'
})
export class ChatService {
    public pusher: Pusher;
    public unseenMessagesCount = 0;
    private currentChannel: any;
    private messageSubject = new Subject<any>();

    constructor(
        private http: HttpClient,
        authService: AuthService
    ) {
        authService.getAccessTokenSilently()?.subscribe((accessToken) => {
            this.pusher = new Pusher(environment.pusherClientSecret, {
                cluster: 'mt1',
                authorizer: (channel) => {
                    return {
                        authorize: (socketId, callback) => {
                            fetch(`${environment.dashboardApiURL}/platform-chat/auth`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${accessToken}`
                                },
                                body: JSON.stringify({
                                    socket_id: socketId,
                                    channel_name: channel.name
                                })
                            })
                                .then(async (response) => {
                                    if (!response.ok) throw new Error('Auth failed');
                                    const data = await response.json();
                                    callback(null, data);
                                })
                                .catch((err) => {
                                    console.error('Pusher auth error:', err);
                                    callback(err, null);
                                });
                        }
                    };
                }
            });
        });
    }

    /** Load all users that Admin had conversations with */
    getUsers(): Observable<any[]> {
        return this.http.get<any[]>(`${environment.dashboardApiURL}/platform-chat/messages?page.index=0&page.size=50`).pipe(tap((res: any) => (this.unseenMessagesCount = res.unseenMessagesCount)));
    }

    /** Load message history with selected user */
    getMessageHistory(chatId: string): Observable<any[]> {
        return this.http.get<any[]>(`${environment.dashboardApiURL}/platform-chat/messages/${chatId}?page.index=0&page.size=1000`);
    }

    /** Send text/media message to a user */
    sendMessage(formData: { messageType: string; message: string }, chatId: string): Observable<any> {
        return this.http.post<any>(`${environment.dashboardApiURL}/platform-chat/messages/${chatId}`, formData);
    }

    seenMessage(userId: string): Observable<any> {
        return this.http.put<any>(`${environment.dashboardApiURL}/platform-chat/messages/${userId}/seen`, null).pipe(tap(() => (this.unseenMessagesCount = 0)));
    }

    sendMessageFile(fileUrl: string, messageType: string, chatId: string): Observable<any> {
        return this.http.post<any>(`${environment.dashboardApiURL}/platform-chat/messages/${chatId}`, {
            fileUrl,
            messageType
        });
    }

    uploadFile(chatId: string, file: File): Observable<any> {
        const fileName = file.name;
        const contentType = file.type;
        let fileUrl = '';
        return this.http
            .post<any>(`${environment.dashboardApiURL}/platform-chat/messages/upload-url`, {
                fileName,
                contentType
            })
            .pipe(
                switchMap(({ url }) => {
                    fileUrl = url.split('?')[0];
                    return this.uploadToS3(url, file);
                }),
                switchMap(() => this.sendMessageFile(fileUrl, contentType, chatId))
            );
    }

    public uploadToS3(url: string, file: File): Observable<{ url: string }> {
        return this.http.put<{ url: string }>(url, file, { headers: { SKIP_AUTH: 'true' } });
    }

    /** Subscribe to a user's channel and emit messages via callback */
    subscribeToUserChannel(userId: string): Observable<any> {
        return new Observable((observer) => {
            if (this.currentChannel) {
                this.pusher.unsubscribe(this.currentChannel.name);
            }

            const channelName = `private-${userId}`;
            this.currentChannel = this.pusher.subscribe(channelName);

            const handler = (data: any) => observer.next(data);
            this.currentChannel.bind('message', handler);

            return () => {
                this.currentChannel.unbind('message', handler);
                this.pusher.unsubscribe(channelName);
                this.currentChannel = null;
            };
        });
    }

    /** Observable message stream (optional alternative to callback-based) */
    getMessageObservable(): Observable<any> {
        return this.messageSubject.asObservable();
    }
}
